import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { EvacuationList } from '@cloudsign/shared';
import type { Evacuation } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useListState } from '../../lib/useList';
import { useSite } from '../../lib/site';
import { Badge, EmptyRow, FilterBar, PageHeader, Pager, SelectField, Spinner, TableWrap, Th } from '../../components/ui';
import { ErrorPanel } from '../../components/ErrorPanel';
import { DateRangePicker, isRangeValid } from '../../components/phase2';
import type { DateRange } from '../../components/phase2';
import { durationText, evacKey } from './api';

const PAGE_SIZE = 25;

/** Seconds between start and end, or null while running. */
export function durationOf(e: Pick<Evacuation, 'startedAt' | 'endedAt'>): number | null {
  if (!e.endedAt) return null;
  return Math.max(0, Math.round((Date.parse(e.endedAt) - Date.parse(e.startedAt)) / 1000));
}

/** Report list: real evacuations and drills are kept apart by the type tabs. */
export function ReportsPage() {
  const list = useListState({ order: 'desc' });
  const { sites } = useSite();
  const [kind, setKind] = useState<'real' | 'drill'>('real');
  const [siteId, setSiteId] = useState('');
  const [range, setRange] = useState<DateRange>({ from: '', to: '' });
  const valid = isRangeValid(range);
  const siteName = (id: string) => sites.find((s) => s.id === id)?.name ?? '';
  const zoneOf = (id: string) => sites.find((s) => s.id === id)?.timeZone;

  const q = useQuery({
    queryKey: [...evacKey, 'list', kind, siteId, range, list.page],
    enabled: valid,
    queryFn: () =>
      api.get('/evacuation', {
        query: { kind, siteId: siteId || undefined, from: range.from || undefined, to: range.to || undefined, page: list.page, pageSize: PAGE_SIZE, order: 'desc' },
        schema: EvacuationList,
      }),
  });

  return (
    <div>
      <PageHeader title={t('evac.reports.title')} description={t('evac.reports.description')} />
      <div role="tablist" aria-label={t('evac.reports.kind')} className="mb-4 flex gap-1 border-b border-slate-200">
        {(['real', 'drill'] as const).map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={kind === k}
            className={`-mb-px min-h-10 border-b-2 px-4 text-sm font-medium ${kind === k ? 'border-[var(--brand-primary)] text-slate-900' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
            onClick={() => {
              setKind(k);
              list.resetPage();
            }}
          >
            {k === 'real' ? t('evac.reports.real') : t('evac.reports.drills')}
          </button>
        ))}
      </div>
      <FilterBar columns="sm:grid-cols-2">
        <SelectField
          label={t('evac.site')}
          value={siteId}
          onChange={(e) => {
            setSiteId(e.target.value);
            list.resetPage();
          }}
        >
          <option value="">{t('evac.reports.allSites')}</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <DateRangePicker
          label={t('evac.reports.dates')}
          value={range}
          onChange={(r) => {
            setRange(r);
            list.resetPage();
          }}
        />
      </FilterBar>

      {q.isError ? <ErrorPanel error={q.error} onRetry={() => void q.refetch()} /> : null}
      {q.isLoading ? <Spinner /> : null}
      {q.data ? (
        <>
          <TableWrap caption={t('evac.reports.table')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('evac.reports.col.started')}</Th>
                <Th>{t('evac.reports.col.site')}</Th>
                <Th>{t('evac.reports.col.startedBy')}</Th>
                <Th>{t('evac.reports.col.completedBy')}</Th>
                <Th>{t('evac.reports.col.duration')}</Th>
                <Th>{t('evac.reports.col.safe')}</Th>
                <Th>{t('evac.reports.col.outstanding')}</Th>
                <Th>{t('evac.reports.col.result')}</Th>
                <Th>{t('evac.reports.col.report')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {q.data.items.length === 0 ? <EmptyRow cols={9} /> : null}
              {q.data.items.map((e) => {
                const outstanding = e.counts.unaccounted + e.counts.missing;
                const when = formatDateTime(e.startedAt, zoneOf(e.siteId));
                return (
                  <tr key={e.id}>
                    <td className="px-4 py-3">{when}</td>
                    <td className="px-4 py-3">{siteName(e.siteId)}</td>
                    <td className="px-4 py-3">{e.startedByLabel}</td>
                    <td className="px-4 py-3">{e.endedByLabel ?? '-'}</td>
                    <td className="px-4 py-3">{e.status === 'active' ? <Badge tone="bad">{t('evac.reports.running')}</Badge> : durationText(durationOf(e), t)}</td>
                    <td className="px-4 py-3 tabular-nums">
                      {e.counts.safe} / {e.counts.total}
                    </td>
                    <td className="px-4 py-3 tabular-nums">{outstanding > 0 ? <Badge tone="bad">{outstanding}</Badge> : outstanding}</td>
                    <td className="px-4 py-3">
                      {e.status === 'active' ? (
                        '-'
                      ) : outstanding === 0 ? (
                        <Badge tone="good">{t('evac.reports.result.allAccounted')}</Badge>
                      ) : (
                        <Badge tone="bad">{t('evac.reports.result.unaccounted', { count: outstanding })}</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link className="font-medium text-[var(--brand-primary)] underline" to={`/reports/evacuations/${e.id}`} aria-label={t('evac.reports.openFor', { site: siteName(e.siteId), time: when })}>
                          {t('evac.reports.open')}
                        </Link>
                        {e.status === 'ended' ? <Badge tone={e.reportReady ? 'good' : 'neutral'}>{e.reportReady ? t('evac.reports.ready') : t('evac.reports.preparing')}</Badge> : null}
                        {e.updatedAfterEnd ? <Badge tone="warn">{t('evac.reports.updated')}</Badge> : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
          <Pager page={q.data.page} pageSize={q.data.pageSize} total={q.data.total} onPage={list.setPage} />
        </>
      ) : null}
    </div>
  );
}
